import { useCurrentOrg, useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useRef, useState } from 'react';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface ApiKeyRow {
  id: string;
  name: string;
  keyPrefix: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function CodeBlock({ language, code }: { language: string; code: string }) {
  return (
    <div className="relative rounded-md bg-slate-900 text-sm">
      <div className="px-4 py-1.5 text-xs text-slate-400 border-b border-slate-700">{language}</div>
      <pre className="overflow-x-auto px-4 py-3 text-slate-200 leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function SettingsApiKeysRoute() {
  const getToken = useSessionTokenGetter();
  const org = useCurrentOrg();
  const [internalOrgId, setInternalOrgId] = useState<string | null>(null);

  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  // Newly created key shown once
  const [revealedKey, setRevealedKey] = useState<{ id: string; name: string; key: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  // Rename
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Revoke confirmation
  const [revoking, setRevoking] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  const fetchKeys = useCallback(async () => {
    if (!org?.clerkOrgId) return;
    const token = await getToken();
    // Also grab the internal org ID from the session for the code snippet
    const meRes = await fetch(`${API}/api/v1/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (meRes.ok) {
      const me = (await meRes.json()) as { session?: { orgId?: string } };
      setInternalOrgId(me?.session?.orgId ?? null);
    }
    const res = await fetch(`${API}/api/v1/api-keys`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<ApiKeyRow[]>;
  }, [org?.clerkOrgId, getToken]);

  useEffect(() => {
    fetchKeys()
      .then((data) => setKeys(data ?? []))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [fetchKeys]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/api-keys`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        setCreateError((err as { error?: string }).error ?? 'Failed to create key');
        return;
      }
      const created = (await res.json()) as {
        id: string;
        name: string;
        key: string;
        prefix: string;
        createdAt: string;
      };
      setRevealedKey({ id: created.id, name: created.name, key: created.key });
      setNewName('');
      // Refresh list
      const updated = await fetchKeys();
      setKeys(updated ?? []);
    } catch (e) {
      setCreateError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(keyId: string) {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/api-keys/${keyId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setKeys((prev) => prev.filter((k) => k.id !== keyId));
      setRevoking(null);
    }
  }

  async function handleRename(keyId: string) {
    if (!renameValue.trim()) return;
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/api-keys/${keyId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: renameValue.trim() }),
    });
    if (res.ok) {
      setKeys((prev) => prev.map((k) => (k.id === keyId ? { ...k, name: renameValue.trim() } : k)));
      setRenaming(null);
      setRenameValue('');
    }
  }

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const orgId = internalOrgId ?? 'YOUR_ORG_ID';

  const curlExample = `curl -X POST "https://api.develio.app/api/v1/orgs/${orgId}/analyses/ingest" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "step1": { "projectName": "Acme Project" }, ... }'`;

  const jsExample = `const res = await fetch(
  "https://api.develio.app/api/v1/orgs/${orgId}/analyses/ingest",
  {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${YOUR_API_KEY}\`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ step1: { projectName: "Acme Project" }, ... }),
  }
);
const { analysisId, results } = await res.json();`;

  const activeKeys = keys.filter((k) => !k.revokedAt);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-400 text-sm">
        Loading API keys…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">API Keys</h1>
        <p className="mt-1 text-sm text-slate-500">
          Generate keys to submit analyses programmatically via the Develio API.{' '}
          <a
            href="/api/docs"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline"
          >
            View API docs →
          </a>
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Revealed key banner — shown once after creation */}
      {revealedKey && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold text-amber-800">
                Save your API key — it won't be shown again
              </p>
              <p className="mt-0.5 text-xs text-amber-700">
                Store it in a password manager or environment variable.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRevealedKey(null)}
              className="text-amber-600 hover:text-amber-800 text-lg leading-none"
            >
              ×
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 rounded border border-amber-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 select-all">
              {revealedKey.key}
            </code>
            <button
              type="button"
              onClick={() => handleCopy(revealedKey.key)}
              className="shrink-0 rounded-md border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Create key form */}
      <form onSubmit={handleCreate} className="mb-6 flex gap-2">
        <input
          ref={nameInputRef}
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Key name, e.g. Zapier integration"
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          maxLength={200}
        />
        <button
          type="submit"
          disabled={creating || !newName.trim()}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {creating ? 'Generating…' : 'Generate Key'}
        </button>
      </form>
      {createError && <p className="mb-4 text-sm text-red-600">{createError}</p>}

      {/* Key table */}
      {activeKeys.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 py-12 text-center">
          <p className="text-sm text-slate-500">No API keys yet.</p>
          <p className="mt-1 text-xs text-slate-400">
            Create a key above to start integrating with the Develio API.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Key</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Last used
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Created</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {activeKeys.map((key) => (
                <tr key={key.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    {renaming === key.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          className="w-40 rounded border border-slate-300 px-2 py-1 text-xs"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(key.id);
                            if (e.key === 'Escape') {
                              setRenaming(null);
                              setRenameValue('');
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRename(key.id)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRenaming(null);
                            setRenameValue('');
                          }}
                          className="text-xs text-slate-400 hover:text-slate-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span className="font-medium text-slate-800">{key.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                      {key.keyPrefix}…
                    </code>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{formatRelative(key.lastUsedAt)}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {new Date(key.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {revoking === key.id ? (
                      <div className="flex items-center justify-end gap-2 text-xs">
                        <span className="text-slate-600">Revoke "{key.name}"?</span>
                        <button
                          type="button"
                          onClick={() => handleRevoke(key.id)}
                          className="font-medium text-red-600 hover:underline"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => setRevoking(null)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setRenaming(key.id);
                            setRenameValue(key.name);
                          }}
                          className="text-xs text-slate-500 hover:text-slate-800"
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          onClick={() => setRevoking(key.id)}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Revoke
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Code snippets */}
      <div className="mt-10">
        <h2 className="mb-4 text-base font-semibold text-slate-800">Quick start</h2>
        <div className="space-y-4">
          <CodeBlock language="curl" code={curlExample} />
          <CodeBlock language="JavaScript (fetch)" code={jsExample} />
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Replace <code className="rounded bg-slate-100 px-1">YOUR_API_KEY</code> with a generated
          key above. See{' '}
          <a
            href="/api/docs"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline"
          >
            full API reference
          </a>{' '}
          for complete request schema and response fields.
        </p>
      </div>
    </div>
  );
}
